<script setup>
/**
 * DesktopCategoryStrip
 * Compact 2-row horizontal category scroller shown above the catalog grid
 * on PC (>= 1280px) when the sidebar is collapsed.
 *   Top row    – Department chips (28px)
 *   Bottom row – Subcategory chips (22px), reserves height when empty
 * Mouse wheel scrolls horizontally; active chip auto-scrolls into view.
 */
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  selectedDepartmentId: { type: String, default: null },
  selectedSubDepartmentId: { type: String, default: null },
  subDepartments: { type: Array, default: () => [] },
  departments: { type: Array, default: () => [] },
  promoDepartmentId: { type: String, default: '__promotions__' },
  subsLoading: { type: Boolean, default: false },
})

const emit = defineEmits(['select-department', 'select-subdepartment'])

const deptScrollRef = ref(null)
const subScrollRef = ref(null)
const activeDeptChipEl = ref(null)
const activeSubChipEl = ref(null)
const deptOverflowStart = ref(false)
const deptOverflowEnd = ref(false)
const subOverflowStart = ref(false)
const subOverflowEnd = ref(false)

const hasSubcategories = computed(
  () =>
    props.selectedDepartmentId !== null &&
    props.selectedDepartmentId !== props.promoDepartmentId &&
    props.subDepartments.length > 0 &&
    !props.subsLoading
)

const currentDepartmentName = computed(() => {
  const dept = props.departments.find((d) => d.id === props.selectedDepartmentId)
  return dept?.name || ''
})

function handleSelectDepartment(dept) {
  emit('select-department', { id: dept.id, name: dept.name })
}

function handleSelectSubdepartment(sub) {
  emit('select-subdepartment', {
    id: sub.id,
    name: sub.name,
    departmentId: props.selectedDepartmentId,
    departmentName: currentDepartmentName.value,
  })
}

// Convert vertical wheel to horizontal scroll (Δy → scrollLeft)
function onWheel(e, scrollEl) {
  if (!scrollEl) return
  // Only intervene when there's actual horizontal overflow
  if (scrollEl.scrollWidth <= scrollEl.clientWidth) return
  if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
  e.preventDefault()
  scrollEl.scrollLeft += e.deltaY
}

function onDeptWheel(e) {
  onWheel(e, deptScrollRef.value)
}

function onSubWheel(e) {
  onWheel(e, subScrollRef.value)
}

// Track overflow for fade hints (RTL: scrollLeft is negative, |scrollLeft| grows toward end)
function updateOverflow(scrollEl, setStart, setEnd) {
  if (!scrollEl) {
    setStart(false)
    setEnd(false)
    return
  }
  const max = scrollEl.scrollWidth - scrollEl.clientWidth
  if (max <= 1) {
    setStart(false)
    setEnd(false)
    return
  }
  const abs = Math.abs(scrollEl.scrollLeft)
  setStart(abs > 1)
  setEnd(abs < max - 1)
}

function updateDeptOverflow() {
  updateOverflow(
    deptScrollRef.value,
    (v) => (deptOverflowStart.value = v),
    (v) => (deptOverflowEnd.value = v)
  )
}

function updateSubOverflow() {
  updateOverflow(
    subScrollRef.value,
    (v) => (subOverflowStart.value = v),
    (v) => (subOverflowEnd.value = v)
  )
}

// Auto-scroll active department chip into view
watch(
  () => props.selectedDepartmentId,
  async () => {
    await nextTick()
    if (activeDeptChipEl.value) {
      activeDeptChipEl.value.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
    updateDeptOverflow()
    // Reset sub scroll on dept change
    if (subScrollRef.value) {
      subScrollRef.value.scrollTo({ left: 0, behavior: 'instant' })
    }
    updateSubOverflow()
  }
)

watch(
  () => props.selectedSubDepartmentId,
  async () => {
    await nextTick()
    if (activeSubChipEl.value) {
      activeSubChipEl.value.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
    updateSubOverflow()
  }
)

watch(
  () => props.departments.length,
  async () => {
    await nextTick()
    updateDeptOverflow()
  }
)

watch(
  () => props.subDepartments.length,
  async () => {
    await nextTick()
    updateSubOverflow()
  }
)

function onResize() {
  updateDeptOverflow()
  updateSubOverflow()
}

onMounted(async () => {
  await nextTick()
  updateDeptOverflow()
  updateSubOverflow()
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <div class="desktop-category-strip">
    <!-- Departments row -->
    <div
      class="strip-row dept-row"
      :class="{ 'overflow-start': deptOverflowStart, 'overflow-end': deptOverflowEnd }"
    >
      <div
        ref="deptScrollRef"
        class="chips-scroll"
        @wheel="onDeptWheel"
        @scroll="updateDeptOverflow"
      >
        <button
          v-for="dept in departments"
          :key="dept.id"
          :ref="
            (el) => {
              if (dept.id === selectedDepartmentId) activeDeptChipEl = el
            }
          "
          class="dept-chip"
          :class="{
            active: dept.id === selectedDepartmentId,
            promo: dept.id === promoDepartmentId && dept.id !== selectedDepartmentId,
          }"
          @click="handleSelectDepartment(dept)"
        >
          {{ dept.name }}
        </button>
      </div>
    </div>

    <!-- Subcategories row (placeholder keeps height stable) -->
    <div
      class="strip-row sub-row"
      :class="{ 'overflow-start': subOverflowStart, 'overflow-end': subOverflowEnd }"
    >
      <div
        v-if="hasSubcategories"
        ref="subScrollRef"
        class="chips-scroll"
        @wheel="onSubWheel"
        @scroll="updateSubOverflow"
      >
        <button
          v-for="sub in subDepartments"
          :key="sub.id"
          :ref="
            (el) => {
              if (sub.id === selectedSubDepartmentId) activeSubChipEl = el
            }
          "
          class="sub-chip"
          :class="{ active: sub.id === selectedSubDepartmentId }"
          @click="handleSelectSubdepartment(sub)"
        >
          {{ sub.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.desktop-category-strip {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 4px;
  padding: 6px var(--spacing-lg);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  animation: stripSlideDown 0.2s ease-out;
}

.strip-row {
  position: relative;
  min-width: 0;
}

/* Edge fade hints when content overflows */
.strip-row::before,
.strip-row::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 24px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1;
}

.strip-row::before {
  right: 0;
  background: linear-gradient(to left, var(--bg-primary) 0%, rgba(255, 255, 255, 0) 100%);
}

.strip-row::after {
  left: 0;
  background: linear-gradient(to right, var(--bg-primary) 0%, rgba(255, 255, 255, 0) 100%);
}

.strip-row.overflow-start::before {
  opacity: 1;
}

.strip-row.overflow-end::after {
  opacity: 1;
}

.chips-scroll {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scroll-behavior: smooth;
  direction: rtl;
}

.chips-scroll::-webkit-scrollbar {
  display: none;
}

.chips-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Department chips (28px tall) */
.dept-row .chips-scroll {
  padding: 1px 0;
}

.dept-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.12s ease;
  user-select: none;
}

.dept-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.dept-chip:active {
  transform: scale(0.97);
}

.dept-chip.active {
  background: var(--gradient-header);
  border-color: transparent;
  color: white;
}

.dept-chip.promo {
  background: linear-gradient(135deg, #fff1f2, #ffe4e6);
  border-color: #fecdd3;
  color: var(--color-error);
}

/* Subcategory chips (22px tall) — fixed height even when empty */
.sub-row {
  min-height: 22px;
}

.sub-row .chips-scroll {
  padding: 0;
}

.sub-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 10px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 12px;
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.12s ease;
  user-select: none;
}

.sub-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.sub-chip:active {
  transform: scale(0.97);
}

.sub-chip.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
  font-weight: var(--font-weight-semibold);
}

@keyframes stripSlideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hide entirely below PC breakpoint */
@media (max-width: 1279px) {
  .desktop-category-strip {
    display: none;
  }
}
</style>
